/**
 * BackupHUB: куда сходятся копии, сколько их и что мимо.
 *
 * Экран отвечает на вопрос, которого нет ни на матрице, ни в связях: какой
 * объём данных компании и каким путём попадает в ОДНО место, откуда его
 * можно разложить дальше. Матрица показывает состояние по объектам, здесь —
 * потоки и масштаб.
 *
 * Три колонки, и разделение между ними — главное содержание экрана.
 * Слева то, что приходит к нам на диск: этими копиями можно распорядиться.
 * Справа то, куда мы их раскладываем дальше. Внизу отдельно — копии, которые
 * есть, но лежат у чужого провайдера, и объекты, у которых копий нет вовсе.
 * Свести первое со вторым в «защищено» значит показать зелёную картину там,
 * где половина хозяйства нам не принадлежит.
 */
import { useRef, useState } from 'react';
import { useParam } from '../router.tsx';
import { useQuery } from '../transport/hooks.ts';
import { FlowCanvas, type Flow } from '../components/FlowCanvas.tsx';
import { agoPhrase, bytes, count } from '../lib/format.ts';
import type {
  GraphNode, GraphSnapshot, TopologyFeed, TopologySnapshot,
} from '../../shared/protocol/topology.ts';
import { HierarchyGraph, GRAPH_MODES, type GraphMode } from '../components/HierarchyGraph.tsx';
import { EditName } from '../components/EditName.tsx';
import { request } from '../transport/socket.ts';
import { Segmented } from '../components/Segmented.tsx';

const HUB = 'hub';

const STATE_WORD: Record<TopologyFeed['state'], string> = {
  active: 'идёт',
  stale: 'отстаёт',
  broken: 'сломан',
  planned: 'план',
  none: 'нет копий',
};

function FeedCard({ feed, share }: { feed: TopologyFeed; share: number }): React.JSX.Element {
  return (
    <article
      className={`node node--feed node--${feed.state}${feed.witnessOf ? ' node--witness' : ''}`}
      data-node={feed.key}
    >
      <header className="node__head">
        <h3 className="node__title">{feed.label}</h3>
        <span className={`node__state node__state--${feed.state}`}>{STATE_WORD[feed.state]}</span>
      </header>
      <p className="node__via">{feed.via}</p>

      {feed.witnessOf ? (
        // Свидетель не носитель объёма, а второй взгляд на те же файлы.
        // Полоса у него была бы обещанием ещё одной копии.
        <p className="node__witness">
          свидетель того же, что «{feed.witnessOf === 'sqlsmb' ? 'Дампы баз SQL Server' : feed.witnessOf}»
        </p>
      ) : (
        <div className="node__metric">
          <span className="node__bytes">{bytes(feed.bytes)}</span>
          <span className="bar bar--slim" aria-hidden="true">
            <span className="bar__fill" style={{ width: `${Math.round(share * 100)}%` }} />
          </span>
        </div>
      )}

      <p className="node__detail">{feed.detail}</p>
      <p className="node__foot">
        {feed.sources} объектов · последняя {agoPhrase(feed.lastAt)}
      </p>
    </article>
  );
}

/**
 * Граф связей — отдельным видом, а не поверх потоков.
 *
 * Экран отвечает на два разных вопроса. Потоки: «сколько и откуда к нам
 * приходит». Связи: «что от чего зависит и где что стоит». Нарисовать их
 * вместе значит получить картинку, по которой не читается ни то ни другое.
 */
function GraphView(): React.JSX.Element {
  const { data, error, loading, reload } = useQuery<GraphSnapshot>('topology.graph');
  const [picked, setPicked] = useState<GraphNode | null>(null);
  const [busy, setBusy] = useState(false);
  // Режим — в адресе: «посмотри вот так» должно работать ссылкой.
  const [mode, setMode] = useParam('mode', 'columns');

  const rename = async (key: string, label: string, note: string): Promise<void> => {
    setBusy(true);
    try {
      await request('inventory.bind', { key, label, note });
      setPicked(null);
      reload();
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="alert alert--error">Ошибка: {error.msg}</p>;
  if (!data) return <p className="muted">{loading ? 'Строим граф…' : 'Нет данных.'}</p>;

  return (
    <>
      <div className="graph__modes">
        <Segmented
          ariaLabel="Как смотреть граф"
          active={mode}
          onSelect={setMode}
          size="sm"
          items={GRAPH_MODES.map((m) => ({ id: m.id, label: m.label, title: m.hint }))}
        />
        <span className="muted graph__mode-hint">
          {GRAPH_MODES.find((m) => m.id === mode)?.hint}
        </span>
      </div>

      {/*
        Правка вынесена НАД графом, а не всплывает над узлом. Поле ввода
        поверх схемы закрывает соседей — ровно те, ради сравнения с которыми
        на граф и смотрят.
      */}
      {picked && (
        <div className="graph__picked">
          <EditName
            objectKey={picked.key}
            shown={picked.label}
            {...(picked.factName ? { factName: picked.factName } : {})}
            {...(picked.note ? { note: picked.note } : {})}
            busy={busy}
            onSave={rename}
          />
          <button type="button" className="button button--ghost button--small"
                  onClick={() => setPicked(null)}>
            Закрыть
          </button>
        </div>
      )}
      <HierarchyGraph snapshot={data} mode={mode as GraphMode} onPick={setPicked} />
    </>
  );
}

export function Topology(): React.JSX.Element {
  // Вид — в адресе: ссылку на граф надо уметь прислать, а обновление страницы
  // не должно возвращать на потоки.
  const [view, setView] = useParam('view', 'graph');
  const { data, error, loading } = useQuery<TopologySnapshot>('topology.get');
  const stage = useRef<HTMLDivElement>(null);

  if (error) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Топология</h2>
        <p className="alert alert--error">Ошибка: {error.msg}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Топология</h2>
        <p className="muted">{loading ? 'Считаем потоки…' : 'Нет данных.'}</p>
      </section>
    );
  }

  const carrying = data.feeds.filter((f) => !f.witnessOf);
  const biggest = Math.max(1, ...carrying.map((f) => f.bytes));
  const used = data.hub.totalBytes > 0 ? data.hub.usedBytes / data.hub.totalBytes : 0;

  const flows: Flow[] = [
    ...data.feeds.map((f) => ({
      from: f.key,
      to: HUB,
      weight: f.witnessOf ? 0 : f.bytes / biggest,
      state: f.state,
    })),
    ...data.outbound.map((o) => ({
      from: HUB,
      to: o.key,
      weight: o.bytes > 0 ? Math.min(1, o.bytes / biggest) : 0.15,
      state: o.state,
    })),
  ];

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <div className="panel__title-row">
          <h2 className="panel__title">Топология</h2>
          <Segmented
            ariaLabel="Что показывать в топологии"
            active={view}
            onSelect={setView}
            items={[
              { id: 'graph', label: 'Связи' },
              { id: 'flows', label: 'Потоки копий' },
            ]}
          />
        </div>
        <p className="hub__lead">
          {bytes(data.totals.inHubBytes)} копий сходятся на один диск — отсюда их можно
          раскладывать дальше централизованно. Ещё {bytes(data.totals.elsewhereBytes)} лежат
          у провайдера и нам не принадлежат.{' '}
          {data.totals.sourcesUncovered > 0 && (
            <b className="hub__gap">
              {data.totals.sourcesUncovered} объектов без копий вообще.
            </b>
          )}
        </p>
      </header>

      {view !== 'flows' ? (
        <div className="panel__scroll"><GraphView /></div>
      ) : (
      <div className="panel__scroll">
        <div className="stage" ref={stage}>
          <FlowCanvas
            flows={flows}
            containerRef={stage}
            deps={`${data.generatedAt}|${data.feeds.length}|${data.outbound.length}`}
          />

          <div className="stage__col stage__col--in">
            <h3 className="stage__caption">Приходит к нам</h3>
            {data.feeds.map((f) => (
              <FeedCard key={f.key} feed={f} share={f.bytes / biggest} />
            ))}
            {data.feeds.length === 0 && <p className="muted">Ни одного потока.</p>}
          </div>

          <div className="stage__col stage__col--hub">
            <article className="node node--hub" data-node={HUB}>
              <span className="node__badge">BackupHUB</span>
              <h3 className="node__title">{data.hub.label}</h3>
              <p className="node__via">{data.hub.detail}</p>

              {data.hub.unavailable ? (
                // «Не измерено» и «места нет» — разные вещи. Нули читаются
                // как заполненный том, то есть врут в сторону спокойствия.
                <p className="node__unknown">{data.hub.unavailable}</p>
              ) : (
                <>
                  <div className="hub__gauge" role="img"
                       aria-label={`занято ${Math.round(used * 100)} процентов`}>
                    <span
                      className={`hub__gauge-fill${used > 0.9 ? ' hub__gauge-fill--hot' : used > 0.75 ? ' hub__gauge-fill--warm' : ''}`}
                      style={{ width: `${Math.min(100, Math.round(used * 100))}%` }}
                    />
                  </div>
                  <p className="hub__numbers">
                    <b>{bytes(data.hub.usedBytes)}</b> занято из {bytes(data.hub.totalBytes)}
                    <span className="muted"> · свободно {bytes(data.hub.freeBytes)}</span>
                  </p>
                </>
              )}

              {data.hub.sharedVolume.length > 0 && (
                <p className="node__detail">
                  {data.hub.sharedVolume.join(', ')} — тот же том, место у них общее,
                  а не в сумме
                </p>
              )}
            </article>
          </div>

          <div className="stage__col stage__col--out">
            <h3 className="stage__caption">Уходит дальше</h3>
            {data.outbound.map((o) => (
              <article key={o.key} className={`node node--out node--${o.state}`} data-node={o.key}>
                <header className="node__head">
                  <h3 className="node__title">{o.label}</h3>
                  <span className={`node__state node__state--${o.state}`}>{STATE_WORD[o.state]}</span>
                </header>
                <p className="node__detail">{o.detail}</p>
                {o.bytes > 0
                  ? <p className="node__foot">{bytes(o.bytes)} · последняя {agoPhrase(o.lastAt)}</p>
                  : <p className="node__foot">ещё ни одного прохода</p>}
              </article>
            ))}
          </div>
        </div>

        <div className="aside">
          <section className="aside__block">
            <h3 className="aside__title">Копии есть, но не у нас</h3>
            <p className="aside__lead">
              Восстановиться можно, переложить к себе — нет. Потеря доступа к аккаунту
              провайдера означает потерю и этих копий.
            </p>
            <ul className="aside__list">
              {data.elsewhere.map((f) => (
                <li key={f.key} className={`aside__item aside__item--${f.state}`}>
                  <span className="aside__name">{f.label}</span>
                  <span className="aside__value">{f.bytes > 0 ? bytes(f.bytes) : '—'}</span>
                  <span className="aside__note">{f.detail}</span>
                </li>
              ))}
              {data.elsewhere.length === 0 && <li className="muted">Таких нет.</li>}
            </ul>
          </section>

          <section className="aside__block">
            <h3 className="aside__title">Без копий вовсе</h3>
            <p className="aside__lead">
              Объекты, у которых ожидается защита, а копий нет ни одной.
            </p>
            <ul className="aside__list">
              {data.gaps.map((g) => (
                <li key={g.key} className="aside__item aside__item--broken">
                  <span className="aside__name">{g.label}</span>
                  <span className="aside__value">{count(g.sources)}</span>
                  <span className="aside__note">{g.detail}</span>
                </li>
              ))}
              {data.gaps.length === 0 && <li className="muted">Таких нет.</li>}
            </ul>
          </section>
        </div>
      </div>
      )}

      <footer className="panel__foot">
        {view !== 'flows' ? (
          <>
            <span className="legend__item">столбцы — уровни владения: площадка → гипервизор → машина → контейнеризация → данные</span>
            <span className="legend__item">наведение подсвечивает предков и потомков</span>
          </>
        ) : (
          <>
        <span className="legend__item">толщина линии — объём потока</span>
        <span className="legend__item">свидетели в объём не идут: это те же файлы</span>
          </>
        )}
        <span className="panel__stamp">
          снимок {new Date(data.generatedAt).toLocaleString('ru-RU')}
        </span>
      </footer>
    </section>
  );
}
